import { useRef, useState } from "react";
import { useCreateOrder } from "@/lib/hooks";
import { ApiError } from "@/lib/api";
import type { CreateOrderInput, CreateOrderResponse } from "@/lib/api";
import { createSubmitGuard, type SubmitGuard } from "@/lib/submit-guard";
import { idempotencyKeySlot, type CheckoutFlow } from "@/lib/idempotency-storage";

/**
 * The checkout's client-side payment choreography, shared by every checkout page
 * so the idempotency rules cannot drift between them (Plan 2, Task 7). Moved
 * verbatim out of CheckoutPage; CheckoutPage.test.tsx pins it end to end and
 * payment-flow.test.ts pins it here.
 *
 * Usage:
 *
 *   const payment = usePaymentSubmission<FormData>({
 *     flow: "replacement",                      // the flow's own key slot (B4)
 *     buildOrder: (data, key) => ({ ...body, idempotencyKey: key }),
 *     onSuccess: (result, data) => navigate("/confirmation", { state: result }),
 *     beforeOrder: async (data) => ({ proceed: true }),   // optional
 *   });
 *   <form onSubmit={handleSubmit(payment.submit)}>
 *   <ErrorMessage message={payment.submitError} />
 *   <button disabled={payment.isSubmitting || payment.isHeld}>
 *
 * What it owns:
 * - The idempotency key for its flow: loaded (or minted) on mount and persisted,
 *   reused as-is on a resubmit, rotated ONLY on confirmed outcomes
 *   (PAYMENT_FAILED / RETRY_NOW / STALE_KEY), cleared on a 201 before onSuccess.
 * - The synchronous submit-guard latch. The whole submission — the page's
 *   pre-order step included — runs under it, so a double-click in one tick is
 *   dropped before any await.
 * - One bounded RETRY_NOW retry with a fresh key, re-running ONLY the order step:
 *   never the pre-order step, whose registration or login must not repeat under a
 *   stale auth closure (F24).
 * - The held state: on a 201 (navigating away) and on PAYMENT_PENDING /
 *   PAYMENT_STUCK (a charge may exist) the latch stays held until reload and
 *   `isHeld` turns true, so the page can render its button disabled (PR #65).
 * - The submit-error message, cleared at the start of every accepted submit.
 *
 * The callbacks are read from the render that produced `submit`, exactly as a
 * page's own onSubmit closure would be.
 */

/** What the page's pre-order step decided. `proceed: false` places no order and
 *  releases the latch; its `message`, when given, becomes the submit error. */
export type PreOrderOutcome = { proceed: true } | { proceed: false; message?: string };

export interface PaymentSubmissionOptions<TData> {
  /** Which checkout this is. Each flow has its own key slot (ruling B4). */
  flow: CheckoutFlow;
  /** The order body for this submission under this key. Called once per order
   *  attempt — the first, and the RETRY_NOW retry with its fresh key. */
  buildOrder: (data: TData, idempotencyKey: string) => CreateOrderInput;
  /** The order is confirmed and the key already cleared. The page navigates. */
  onSuccess: (result: CreateOrderResponse, data: TData) => void;
  /** Optional step before the order, under the same latch and the same error
   *  handling (the public checkout's session-ended refusal and registration).
   *  Never re-run by the RETRY_NOW retry. A thrown error goes through the shared
   *  error tail, so a step that wants its own handling (EMAIL_EXISTS) catches it
   *  and returns `{ proceed: false }`. */
  beforeOrder?: (data: TData) => Promise<PreOrderOutcome>;
}

export interface PaymentSubmission<TData> {
  /** Submit the form values. A call while the latch is held is dropped. */
  submit: (data: TData) => Promise<void>;
  /** True from an accepted submit until it settles. */
  isSubmitting: boolean;
  /** True once the page is held: on success, and on PAYMENT_PENDING /
   *  PAYMENT_STUCK. Stays true until reload; render the submit button disabled. */
  isHeld: boolean;
  /** The message to show the vendor, or null. */
  submitError: string | null;
}

/**
 * Server codes that mean "a charge may already exist".
 *
 * The submit guard is HELD for every one of them, so the form stays dead and the
 * vendor cannot resubmit a payment that may already have been taken.
 *
 * The key never rotates on AMBIGUITY: a reload converges on the same record and
 * the same Vanco payment, which is what retired PAYMENT_UNCERTAIN. Rotation
 * happens only on CONFIRMED outcomes — a definitive decline (PAYMENT_FAILED),
 * or a server command (RETRY_NOW / STALE_KEY) after proving no charge exists
 * under the old key.
 *
 * A Map, not an object literal: this is keyed by a server-supplied string, and
 * an object literal would resolve "constructor" or "toString" off the prototype
 * chain to a truthy non-string.
 */
const PAYMENT_HELD_MESSAGES = new Map<string, string>([
  [
    "PAYMENT_PENDING",
    "We could not confirm this payment yet. Please do not resubmit — reload this page in a minute and we will finish it for you.",
  ],
  [
    "PAYMENT_STUCK",
    "We are verifying this payment and will contact you shortly. Please do not resubmit or you may be charged twice.",
  ],
]);

export function usePaymentSubmission<TData>(
  options: PaymentSubmissionOptions<TData>,
): PaymentSubmission<TData> {
  const { flow, buildOrder, onSuccess, beforeOrder } = options;
  const createOrder = useCreateOrder();
  const keySlot = idempotencyKeySlot(flow);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Mirrors the submit latch for rendering: the latch is a ref, so on its own
  // a held page leaves the button looking live while it ignores clicks.
  const [isHeld, setIsHeld] = useState(false);

  // Lazily created once. Passing the guard straight to useRef would re-evaluate
  // the initialiser on every render and throw the new guard away.
  const submitGuardRef = useRef<SubmitGuard | null>(null);
  submitGuardRef.current ??= createSubmitGuard();

  // Persisted, so a reload converges on the same payment instead of minting a
  // second one. That reload is the likeliest act of a vendor who was just told we
  // could not confirm their payment.
  const idempotencyKeyRef = useRef<string | null>(null);
  idempotencyKeyRef.current ??= keySlot.loadOrCreate();

  /** Build the order input and POST it, owning the whole order lifecycle:
   *  on success it clears the key and hands over to the page. Separate so the
   *  RETRY_NOW retry re-runs ONLY this — never the pre-order step (registration
   *  or login), which must not repeat under a stale auth closure (F24). */
  async function submitOrder(data: TData, idempotencyKey: string): Promise<void> {
    const result = await createOrder.mutateAsync(buildOrder(data, idempotencyKey));

    // The order is confirmed: the next checkout is a NEW payment.
    keySlot.clear();
    idempotencyKeyRef.current = null;
    onSuccess(result, data);
  }

  /** Shared catch-tail for order-submission failures (first attempt AND the
   *  bounded RETRY_NOW retry). Returns true when the submit guard must stay
   *  HELD — a charge may already exist and the form must stay dead. */
  function surfaceOrderError(err: unknown): boolean {
    if (err instanceof ApiError) {
      const heldMessage = PAYMENT_HELD_MESSAGES.get(err.code);
      if (heldMessage) {
        // AMBIGUITY: the key is deliberately NOT rotated — a reload or retry
        // must converge on the same server record and the same Vanco payment.
        setSubmitError(heldMessage);
        return true;
      }
      if (err.code === "DUPLICATE_ORDER") {
        setSubmitError(
          "An identical order was submitted in the last 30 minutes. Check your receipts before trying again.",
        );
        return false;
      }
      if (err.code === "CLAIM_UNAVAILABLE") {
        setSubmitError(
          "We are temporarily unable to process payments. Please try again in a few minutes.",
        );
        return false;
      }
      if (err.code === "PAYMENT_FAILED") {
        // CONFIRMED decline: rotate. Vanco binds the key to the first request
        // body (400/7030 on same-key/different-token, probed 2026-07-22), and a
        // corrected-card retry always re-tokenizes — so the old key can never
        // charge again anyway. Ambiguous outcomes never reach this branch.
        // decisions/2026-07-22-decline-rotates-idempotency-key.md
        idempotencyKeyRef.current = keySlot.rotate();
        setSubmitError(
          `Payment failed: ${err.message}. Please check your payment details and try again.`,
        );
        return false;
      }
      if (err.code === "TOKENIZE_FAILED") {
        // No charge, and the key never reached Vanco's create — safe to keep.
        setSubmitError(
          `Payment failed: ${err.message}. Please check your payment details and try again.`,
        );
        return false;
      }
      setSubmitError(err.message);
      return false;
    }
    setSubmitError("An unexpected error occurred. Please try again.");
    return false;
  }

  async function submit(data: TData): Promise<void> {
    // Must come before any await: a double-click puts two calls in the same
    // tick, and the second must be dropped here. isSubmitting cannot do this —
    // React has not re-rendered the disabled button yet.
    const guard = submitGuardRef.current;
    if (!guard || !guard.tryAcquire()) {
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);
    let holdGuard = false;

    // Narrow the ref once. Reused as-is on a resubmit; rotation happens ONLY on
    // confirmed outcomes (PAYMENT_FAILED / RETRY_NOW / STALE_KEY).
    const idempotencyKey = idempotencyKeyRef.current ?? keySlot.loadOrCreate();
    idempotencyKeyRef.current = idempotencyKey;

    try {
      if (beforeOrder) {
        const outcome = await beforeOrder(data);
        if (!outcome.proceed) {
          // No order is placed, so no charge is possible here.
          if (outcome.message !== undefined) setSubmitError(outcome.message);
          return;
        }
      }

      await submitOrder(data, idempotencyKey);
      holdGuard = true; // navigating away; the form must stay dead
    } catch (err) {
      if (err instanceof ApiError && err.code === "RETRY_NOW") {
        // Proven no charge under the old key (History affirmatively absent +
        // authorization expired + records cleared). Rotate and retry ONCE,
        // automatically — never in a loop. The retry runs ONLY submitOrder, and
        // its own failure surfaces through the shared tail, never re-retried.
        const freshKey = keySlot.rotate();
        idempotencyKeyRef.current = freshKey;
        try {
          await submitOrder(data, freshKey);
          holdGuard = true; // successful retry handed over + cleared the key
        } catch (retryErr) {
          holdGuard = surfaceOrderError(retryErr);
        }
        return;
      }
      if (err instanceof ApiError && err.code === "STALE_KEY") {
        // An earlier order under this key already completed and was PAID.
        // Rotate so the NEXT submit is clean, but make the vendor look first —
        // auto-retrying would charge the edited cart on top of the paid order.
        idempotencyKeyRef.current = keySlot.rotate();
        setSubmitError(
          "An earlier order from this page already went through — check your email or your receipts before submitting again, or you may be charged twice. If this is a separate, additional order, review it and press Place Order once more.",
        );
        return;
      }
      holdGuard = surfaceOrderError(err);
    } finally {
      // Held on success — we are navigating away and the form must stay dead.
      // Also held on PENDING/STUCK so a possibly-charged payment cannot be
      // resubmitted. Released on every other failure so the vendor can fix
      // details and retry. finally also covers the early returns above.
      setIsSubmitting(false);
      if (!holdGuard) {
        guard.release();
      } else {
        setIsHeld(true);
      }
    }
  }

  return { submit, isSubmitting, isHeld, submitError };
}
